import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Receipt } from 'lucide-react';
import { adminDeals, adminSymbols } from '@/api/admin';
import type { Deal, DealType, FollowMode, Money } from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/widgets/Pill';
import { ApiError } from '@/lib/http';
import { downloadCsv } from '@/lib/csv';
import { fmtDateTime, fmtMoney, fmtNum, moneyToNumber } from '@/lib/format';
import { DealDetailDialog } from '@/features/orders/DealDetailDialog';

const TIME_RANGES = [
  { label: '今日', hours: 24 },
  { label: '近 7 天', hours: 24 * 7 },
  { label: '近 30 天', hours: 24 * 30 },
  { label: '全部', hours: 0 },
];

const SIDE_FILTERS: { label: string; value: 'ALL' | 'BUY' | 'SELL' }[] = [
  { label: '全部', value: 'ALL' },
  { label: '买入 BUY', value: 'BUY' },
  { label: '卖出 SELL', value: 'SELL' },
];

const TYPE_FILTERS: { label: string; value: 'ALL' | DealType }[] = [
  { label: '全部', value: 'ALL' },
  { label: '开仓', value: 'OPEN' },
  { label: '平仓', value: 'CLOSE' },
];

const DEAL_TYPE_LABEL: Record<DealType, string> = {
  OPEN: '开仓',
  CLOSE: '平仓',
  PARTIAL_CLOSE: '部分平仓',
};

const DEAL_TYPE_TONE: Record<DealType, 'up' | 'warn' | 'neutral'> = {
  OPEN: 'neutral',
  CLOSE: 'up',
  PARTIAL_CLOSE: 'warn',
};

const FOLLOW_MODE_LABEL: Record<FollowMode, string> = { REVERSE: '反向', FOLLOW: '跟随' };
const FOLLOW_MODE_TONE: Record<FollowMode, 'brand' | 'up'> = { REVERSE: 'brand', FOLLOW: 'up' };

const PAGE_SIZE = 50;

/**
 * 真实成交明细 / 成交流水(§9.8 扩展)— 真实 hedging 账户逐笔成交台账(平台视角)。
 * 侧栏归属「真实订单」版块(与仪表板 / 资金账户大屏 / 平台经营 / 报表中心同组)。
 *
 * 管理端内部直说真实账户。本页是【真实账户/平台视角】:每笔成交标注它是反向还是跟随执行、
 * 用户下单手数经多大比例缩放成真实手数,逐笔可见成交价、盈亏、本笔费用、本笔净盈亏。
 * 用户级视角(某用户自己的下单/持仓/已平)由「用户管理」版块下的「用户订单」页承接,故本页不再以用户为中心,
 * userId 下沉到行内「成交详情」弹窗(可跳『用户订单』回溯)。数据源 GET /admin/api/v1/deals。
 */
export function Orders() {
  const [hours, setHours] = useState<number>(24);
  const [side, setSide] = useState<'ALL' | 'BUY' | 'SELL'>('ALL');
  const [dealType, setDealType] = useState<'ALL' | DealType>('ALL');
  const [symbolId, setSymbolId] = useState<string>('ALL');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<Deal | null>(null);

  const fromIso = useMemo(
    () => (hours > 0 ? new Date(Date.now() - hours * 3600_000).toISOString() : undefined),
    [hours],
  );

  const symbolsQ = useQuery({ queryKey: ['admin', 'symbols'], queryFn: adminSymbols.list });
  const symbolName = (id: string) =>
    symbolsQ.data?.find((s) => s.symbolId === id)?.symbolName ?? id;

  // 任一筛选变更回到第 1 页。
  const setHoursP = (v: number) => { setHours(v); setPage(1); };
  const setSideP = (v: typeof side) => { setSide(v); setPage(1); };
  const setTypeP = (v: typeof dealType) => { setDealType(v); setPage(1); };
  const setSymbolP = (v: string) => { setSymbolId(v); setPage(1); };

  const listQ = useQuery({
    queryKey: ['admin', 'deals', hours, side, dealType, symbolId, page],
    queryFn: () =>
      adminDeals.list({
        page,
        pageSize: PAGE_SIZE,
        ...(fromIso ? { from: fromIso } : {}),
        ...(side !== 'ALL' ? { side } : {}),
        ...(dealType !== 'ALL' ? { dealType } : {}),
        ...(symbolId !== 'ALL' ? { symbolId } : {}),
      }),
    placeholderData: (prev) => prev,
    refetchInterval: 30_000,
  });

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // 本页合计(非全量):全部由当前页 items 前端 reduce 得出。费用合计只含真实账户 commission+swap;
  // 服务费是虚拟逐用户口径(仅 VALUATION_CLOSE 行有值,本真实台账恒 null),故全程不在本页出现。
  // 全量真实盈亏请看仪表板/资金账户大屏的 realAccountNet(§13)。
  const summary = useMemo(() => {
    let openCount = 0, closeCount = 0, partialCount = 0, reverseCount = 0, followCount = 0;
    let realLots100 = 0, netPnlUsd = 0, hasNet = false, feeUsd = 0, hasFee = false;
    for (const d of items) {
      realLots100 += d.volume;
      if (d.dealType === 'OPEN') openCount += 1;
      else if (d.dealType === 'CLOSE') closeCount += 1;
      else partialCount += 1;
      if (d.followMode === 'REVERSE') reverseCount += 1;
      else if (d.followMode === 'FOLLOW') followCount += 1;
      if (d.netPnl?.value != null) { netPnlUsd += moneyToNumber(d.netPnl); hasNet = true; }
      if (d.commission?.value != null || d.swap?.value != null) {
        feeUsd += moneyToNumber(d.commission) + moneyToNumber(d.swap);
        hasFee = true;
      }
    }
    return {
      count: items.length,
      openCount, closeCount, partialCount, reverseCount, followCount,
      realLots: realLots100 / 100,
      netPnl: hasNet ? ({ value: Math.round(netPnlUsd * 100), digits: 2 } as Money) : null,
      fee: hasFee ? ({ value: Math.round(feeUsd * 100), digits: 2 } as Money) : null,
    };
  }, [items]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
            <Receipt className="h-5 w-5 text-brand" />
            真实成交明细
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            真实账户逐笔成交台账 · 反向 / 跟随执行 · 缩放手数 · 净盈亏,点击任意行查看对账详情(§9.8)
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={items.length === 0}
          title={`仅导出当前页 ${items.length} 条(每页 ${PAGE_SIZE} 条);翻页后再次导出可获取其余数据`}
          onClick={() =>
            downloadCsv('deals', items, [
              { key: 'executedAt', header: '时间', format: (v) => fmtDateTime(v as string) },
              { key: 'symbolId', header: '品种', format: (v) => symbolName(String(v)) },
              { key: 'followMode', header: '模式', format: (v) => (v ? FOLLOW_MODE_LABEL[v as FollowMode] ?? String(v) : '') },
              { key: 'side', header: '真实方向' },
              { key: 'dealType', header: '类型', format: (v) => DEAL_TYPE_LABEL[v as DealType] ?? String(v) },
              { key: 'virtualVolume', header: '用户手数', format: (v) => (v == null ? '' : (Number(v) / 100).toFixed(2)) },
              { key: 'followRatioUsed', header: '比例', format: (v) => (v == null ? '' : fmtNum(Number(v), 2)) },
              { key: 'volume', header: '真实手数', format: (v) => (Number(v) / 100).toFixed(2) },
              { key: 'price', header: '成交价', format: (v) => fmtNum(Number(v), 2) },
              { key: 'pnl', header: '盈亏', format: (v) => csvMoney(v as Money | null) },
              { key: 'commission', header: '佣金', format: (v) => csvMoney(v as Money | null) },
              { key: 'swap', header: '库存费', format: (v) => csvMoney(v as Money | null) },
              { key: 'netPnl', header: '净盈亏', format: (v) => csvMoney(v as Money | null) },
              { key: 'userId', header: '触发用户ID' },
              { key: 'orderId', header: '真实订单号' },
              { key: 'positionId', header: '真实仓ID' },
            ])
          }
        >
          <Download className="h-4 w-4" />
          导出当前页 CSV
        </Button>
      </header>

      {/* Filter chips */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-ink-3 mr-1">时间</span>
          {TIME_RANGES.map((t) => (
            <Button key={t.label} variant={hours === t.hours ? 'default' : 'outline'} size="sm" onClick={() => setHoursP(t.hours)}>
              {t.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-x-6 gap-y-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-ink-3 mr-1" title="筛选的是真实账户成交方向,非用户下单方向">真实方向</span>
            {SIDE_FILTERS.map((f) => (
              <Button key={f.value} variant={side === f.value ? 'default' : 'outline'} size="sm" onClick={() => setSideP(f.value)}>
                {f.label}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-ink-3 mr-1">类型</span>
            {TYPE_FILTERS.map((f) => (
              <Button key={f.value} variant={dealType === f.value ? 'default' : 'outline'} size="sm" onClick={() => setTypeP(f.value)}>
                {f.label}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-3 mr-1">品种</span>
            <select
              value={symbolId}
              onChange={(e) => setSymbolP(e.target.value)}
              className="h-8 rounded-md border border-line bg-bg-3 px-2 text-sm text-ink-1"
            >
              <option value="ALL">全部</option>
              {(symbolsQ.data ?? []).map((s) => (
                <option key={s.symbolId} value={s.symbolId}>{s.symbolName}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 本页合计(非全量)— 页内速览 */}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-md border border-line bg-bg-3 px-4 py-2.5 text-sm">
          <span className="text-xs text-ink-3">本页合计 · 最多 {PAGE_SIZE} 条 · 非全量</span>
          <Stat label="成交">
            <span className="num text-ink-1">{summary.count}</span>
            <span className="num text-ink-3 ml-1 text-xs">开{summary.openCount}/平{summary.closeCount}/部分{summary.partialCount}</span>
          </Stat>
          <Stat label="模式">
            <span className="num text-brand-dim">反向 {summary.reverseCount}</span>
            <span className="num text-up-dim ml-2">跟随 {summary.followCount}</span>
          </Stat>
          <Stat label="真实手数">
            <span className="num text-ink-1">{summary.realLots.toFixed(2)} 手</span>
          </Stat>
          <Stat label="净盈亏">
            <SummaryMoney m={summary.netPnl} />
          </Stat>
          <Stat label="费用">
            <SummaryMoney m={summary.fee} neutralPositive />
          </Stat>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            成交流水 · {listQ.data ? total : '—'} 条
            {total > 0 && (
              <span className="ml-2 text-xs text-ink-3 font-normal num">第 {page}/{totalPages} 页</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {listQ.isError && (
            <div className="px-4 py-3 text-sm text-down-dim">
              无法加载:{listQ.error instanceof ApiError ? listQ.error.message : '未知错误'}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left font-normal">时间</th>
                  <th className="px-3 py-2 text-left font-normal">品种</th>
                  <th className="px-3 py-2 text-left font-normal">模式</th>
                  <th className="px-3 py-2 text-center font-normal">真实方向</th>
                  <th className="px-3 py-2 text-center font-normal">类型</th>
                  <th className="px-3 py-2 text-right font-normal" title="用户下单手数 → 缩放后真实手数(R=round(用户手数×比例))">虚拟 → 真实 手数</th>
                  <th className="px-3 py-2 text-right font-normal">成交价</th>
                  <th className="px-3 py-2 text-right font-normal">盈亏</th>
                  <th className="px-3 py-2 text-right font-normal" title="本笔成交佣金+库存费;开仓行也计入已发生佣金">费用</th>
                  <th className="px-3 py-2 text-right font-normal">净盈亏</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <DealRow key={d.id} d={d} symbolName={symbolName(d.symbolId)} onSelect={setDetail} />
                ))}
                {items.length === 0 && !listQ.isLoading && (
                  <tr>
                    <td colSpan={10} className="px-3 py-8 text-center text-ink-3">没有符合条件的成交</td>
                  </tr>
                )}
                {listQ.isLoading && (
                  <tr>
                    <td colSpan={10} className="px-3 py-8 text-center text-ink-3">加载中...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-line">
              <Button variant="outline" size="sm" disabled={page <= 1 || listQ.isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                上一页
              </Button>
              <span className="text-xs text-ink-3 num">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages || listQ.isFetching} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                下一页
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <DealDetailDialog
        deal={detail}
        symbolName={detail ? symbolName(detail.symbolId) : ''}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}

function DealRow({ d, symbolName, onSelect }: { d: Deal; symbolName: string; onSelect: (d: Deal) => void }) {
  const feeTotal = moneyToNumber(d.commission) + moneyToNumber(d.swap);
  const hasFee = (d.commission?.value != null) || (d.swap?.value != null);
  const hasScaling = d.virtualVolume != null && d.virtualVolume > 0 && d.followRatioUsed != null && d.followRatioUsed > 0;
  return (
    <tr className="border-t border-line row-hover cursor-pointer" onClick={() => onSelect(d)}>
      <td className="px-3 py-1.5 num text-ink-2">{fmtDateTime(d.executedAt)}</td>
      <td className="px-3 py-1.5 text-ink-1">{symbolName}</td>
      <td className="px-3 py-1.5">
        {d.followMode
          ? <Pill tone={FOLLOW_MODE_TONE[d.followMode]}>{FOLLOW_MODE_LABEL[d.followMode]}</Pill>
          : <span className="text-ink-3">—</span>}
      </td>
      <td className="px-3 py-1.5 text-center">
        <Pill tone={d.side === 'BUY' ? 'up' : 'down'}>{d.side}</Pill>
      </td>
      <td className="px-3 py-1.5 text-center">
        <Pill tone={DEAL_TYPE_TONE[d.dealType]}>{DEAL_TYPE_LABEL[d.dealType]}</Pill>
      </td>
      <td className="px-3 py-1.5 num text-right">
        {hasScaling ? (
          <span>
            <span className="text-ink-3">{(d.virtualVolume! / 100).toFixed(2)}</span>
            <span className="text-ink-3 mx-1">→</span>
            <span className="text-ink-1">{(d.volume / 100).toFixed(2)}</span>
            <span className="text-ink-3 text-[10px] ml-1">×{fmtNum(d.followRatioUsed!, 2)}</span>
          </span>
        ) : (
          <span className="text-ink-1">{(d.volume / 100).toFixed(2)}</span>
        )}
      </td>
      <td className="px-3 py-1.5 num text-right text-ink-1">{fmtNum(d.price, 2)}</td>
      <td className="px-3 py-1.5 num text-right"><MoneyCell m={d.pnl} /></td>
      <td className="px-3 py-1.5 num text-right">
        {hasFee ? (
          <span className={feeTotal < 0 ? 'text-down' : 'text-ink-1'}>
            {fmtMoney({ value: Math.round(feeTotal * 100), digits: 2 }, { sign: true })}
          </span>
        ) : (
          <span className="text-ink-3">—</span>
        )}
      </td>
      <td className="px-3 py-1.5 num text-right"><MoneyCell m={d.netPnl} emphasized /></td>
    </tr>
  );
}

/** 盈亏 / 净盈亏单元格;无值(开仓成交)显示灰色 —,有值按正负染色。emphasized=结论列加重。 */
function MoneyCell({ m, emphasized }: { m: Money | null | undefined; emphasized?: boolean }) {
  if (!m || m.value == null) return <span className="text-ink-3">—</span>;
  const n = moneyToNumber(m);
  const tone = n > 0 ? 'text-up' : n < 0 ? 'text-down' : 'text-ink-1';
  return <span className={`${tone} ${emphasized ? 'font-medium' : ''}`}>{fmtMoney(m, { sign: true })}</span>;
}

/** 本页合计的一个统计项:标签 + 值。 */
function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="text-xs text-ink-3">{label}</span>
      {children}
    </span>
  );
}

/** 合计金额:无值显灰 —,按正负染色;neutralPositive=正数(如费用合计为 0)不染绿。 */
function SummaryMoney({ m, neutralPositive }: { m: Money | null; neutralPositive?: boolean }) {
  if (!m || m.value == null) return <span className="text-ink-3">—</span>;
  const n = moneyToNumber(m);
  const tone = n < 0 ? 'text-down' : (neutralPositive || n === 0) ? 'text-ink-1' : 'text-up';
  return <span className={`num ${tone}`}>{fmtMoney(m, { sign: true })}</span>;
}

/** CSV 导出用:Money → 纯数字字符串(无符号前缀,Excel 友好) */
function csvMoney(m: Money | null | undefined): string {
  if (!m || m.value == null) return '';
  return moneyToNumber(m).toFixed(2);
}
